import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getApiInstance } from "./config";
import { findQueryByPrefix } from "../utils/findQueries";
import { FieldExtraOption, FormatedField } from "../types/fields.types";
import { getStore } from "../store/store";

export type FileData = {
  file: File;
  entity: string;
  itemId: string | number;
  folder?: string;
  instanceToUse?: string;
};

export const useFileUploadToField = (field: FormatedField, instanceToUse?: FieldExtraOption["instanceToUse"], additionalData?: FieldExtraOption["additionalData"]) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: FileData) => uploadFile(file, field, instanceToUse, additionalData),
    onSuccess(data, variables) {
      queryClient.invalidateQueries({
        predicate: (query) => {
          return findQueryByPrefix(query, `${variables.entity}`);
        },
      });
      
      if (variables.entity === "directus_users") {
        queryClient.invalidateQueries({
          queryKey: ["user"],
        });
      }
    },
  });
};

const uploadFile = async (file: FileData, field: FormatedField, instanceToUse?: string, additionalData?: Record<string, any>) => {
  const apiInstance = getApiInstance(instanceToUse);

  const formData = new FormData();
  if (file.folder) {
    formData.append("folder", file.folder);
  }
  formData.append("file", file.file);

  const response = await apiInstance({
    method: "post",
    url: "/files",
    data: formData,
  });

  const uploadedFile = response.data.data;

  const fileAssociated = await associateFileToField(uploadedFile.id, file, field, instanceToUse, additionalData);

  return fileAssociated;
};

const associateFileToField = async (directusFileId: number, file: FileData, field: FormatedField, instanceToUse?: string, additionalData?: Record<string, any>) => {
  const apiInstance = getApiInstance(instanceToUse);
  const data = dataToAssociateFile(directusFileId, field, additionalData);
  const {notifications} = getStore();

  let url = `items/${file.entity}/${file.itemId}`;
  if (file.entity === "directus_users") url = `users/${file.itemId}`;

  try{
    const response = await apiInstance({
        method: "patch",
        url: url,
        data: data,
      });
    
      return response.data.data;
  }catch(error: any){
    notifications.setNotification({
      isOpen: true,
      message: error?.response?.data?.errors[0]?.message || 'Error associating file to field',
      severity: 'error',
    });
  }

};

const dataToAssociateFile = (directusFileId: number, field: FormatedField, additionalData?: Record<string, any>) => {
  const fieldType = field.settings.interface;

  if (fieldType === "file" || fieldType === "file-image") {
    return {
      [field.field]: directusFileId,
    };
  } else if (fieldType === "files") {
    if (additionalData) {
      return {
        [field.field]: {
          create: [
            {
              directus_files_id: { id: directusFileId },
              ...additionalData,
            },
          ],
          update: [],
        },
      };
    } else {
      return {
        [field.field]: {
          create: [
            {
              directus_files_id: { id: directusFileId },
            },
          ],
          update: [],
        },
      };
    }
  }
};
